import { useCallback } from "react";
import {
  useCorrectFertilityGoal,
  type CorrectFertilityGoalPayload,
} from "@src/packages/fertility-intent";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import type { Patient } from "@src/packages/patients/patients.types";
import type {
  IntentPickerPrimaryIntent,
  IntentPickerStage,
} from "./IntentPickerModal";

/**
 * The texting agent still reads the legacy `homepageStage`, so it is kept in
 * step with the onboarding stage the picker now records.
 */
const LEGACY_HOMEPAGE_STAGE: Record<
  IntentPickerStage,
  NonNullable<Patient["homepageStage"]>
> = {
  "exploring-options": "explore",
  "trying-naturally": "explore",
  "getting-ready": "before-treatment",
  "in-treatment": "in-care",
};

/**
 * Saves an intent-picker choice: the goal (through a versioned correction when
 * one is already recorded) and the onboarding stage that Home and the server
 * projection read. `save` resolves false when it fails so the picker stays
 * open; `isSaving` and `hasError` describe the same mutations for the picker.
 */
export function useSaveIntentChoice() {
  const { data: patient } = usePatient();
  const updatePatient = useUpdatePatient();
  const correctFertilityGoal = useCorrectFertilityGoal();
  const fertilityIntent = patient?.fertilityIntent;

  const save = useCallback(
    async (
      goal: string,
      stage: IntentPickerStage | undefined,
      primaryIntent?: IntentPickerPrimaryIntent,
    ): Promise<boolean> => {
      const homepageStage = stage ? LEGACY_HOMEPAGE_STAGE[stage] : "explore";
      try {
        if (fertilityIntent) {
          const pathChanged =
            primaryIntent !== undefined &&
            fertilityIntent.primaryIntent !== primaryIntent;
          if (fertilityIntent.goal !== goal || pathChanged) {
            await correctFertilityGoal.mutateAsync({
              payload: {
                expectedVersion: fertilityIntent.version,
                goal: goal as CorrectFertilityGoalPayload["goal"],
                ...(primaryIntent ? { primaryIntent } : {}),
                correctionSource: "patient",
                reason: "user_requested",
              },
            });
          }
          await updatePatient.mutateAsync({
            ...(stage
              ? {
                  fertilityIntent: {
                    stage,
                    // Resent unchanged: until dashboard-api#1883 is released,
                    // the API writes null for an omitted completedAt and would
                    // erase the signup timestamp on this partial save.
                    ...(fertilityIntent.completedAt
                      ? { completedAt: fertilityIntent.completedAt }
                      : {}),
                  },
                }
              : {}),
            homepageStage,
          });
        } else {
          await updatePatient.mutateAsync({
            fertilityIntent: {
              goal: goal as NonNullable<Patient["fertilityIntent"]>["goal"],
              ...(primaryIntent ? { primaryIntent } : {}),
              ...(stage ? { stage } : {}),
            },
            homepageStage,
          });
        }
        return true;
      } catch {
        return false;
      }
    },
    [correctFertilityGoal, fertilityIntent, updatePatient],
  );

  return {
    save,
    isSaving: correctFertilityGoal.isPending || updatePatient.isPending,
    hasError: correctFertilityGoal.isError || updatePatient.isError,
  };
}
